
import { useState } from "react";
import { Module, Lesson } from "@/types/course";
import { Button } from "@/components/ui/button";
import { Plus, GripVertical, Pencil, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface LessonListProps {
  module: Module | null;
  selectedLessonId: string | null;
  onSelectLesson: (lessonId: string | null) => void;
  onAddLesson: () => void;
  onUpdateLesson: (updatedLesson: Lesson) => void;
}

export function LessonList({ 
  module, 
  selectedLessonId, 
  onSelectLesson, 
  onAddLesson,
  onUpdateLesson
}: LessonListProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState<Lesson | null>(null);

  // Guard against undefined module
  if (!module) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8 text-muted-foreground">
          No module selected. Please select a module to view its lessons.
        </div>
      </div>
    );
  }

  const confirmDeleteLesson = (lesson: Lesson, e: React.MouseEvent) => {
    e.stopPropagation();
    setLessonToDelete(lesson);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteLesson = async () => {
    if (!lessonToDelete || !lessonToDelete.id) {
      toast.error("Invalid lesson selected for deletion");
      setIsDeleteDialogOpen(false);
      return;
    }

    try {
      console.log("Deleting lesson with ID:", lessonToDelete.id);
      
      const { error } = await supabase
        .from("lessons")
        .delete()
        .eq("id", lessonToDelete.id);

      if (error) throw error;

      // If the deleted lesson was selected, deselect it
      if (selectedLessonId === lessonToDelete.id) {
        onSelectLesson(null);
      }
      
      // Update the module's lessons
      const updatedLessons = module.lessons?.filter(l => l.id !== lessonToDelete.id) || [];
      module.lessons = updatedLessons;
      
      setIsDeleteDialogOpen(false);
      setLessonToDelete(null);
      toast.success("Lesson deleted successfully");
    } catch (error) {
      console.error("Error deleting lesson:", error);
      toast.error("Failed to delete lesson");
      setIsDeleteDialogOpen(false);
    }
  };

  // Safe access to module title
  const moduleTitle = module?.title || "this module";

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Lessons in {moduleTitle}</h3>
        <Button 
          size="sm" 
          onClick={onAddLesson}
        >
          <Plus className="h-4 w-4 mr-2" /> Add Lesson
        </Button>
      </div>

      <div className="space-y-2">
        {!module.lessons || module.lessons.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No lessons in this module yet. Add your first lesson to get started.
          </div>
        ) : (
          module.lessons.map((lesson) => (
            <div 
              key={lesson.id}
              className={`flex items-center justify-between p-3 border rounded-md hover:bg-muted/50 cursor-pointer ${
                selectedLessonId === lesson.id ? 'border-primary/50 bg-primary/5' : ''
              }`}
              onClick={() => onSelectLesson(lesson.id)}
            >
              <div className="flex items-center gap-3 flex-1">
                <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                <div>
                  <p className="font-medium">{lesson.title}</p>
                  {lesson.description && (
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {lesson.description}
                    </p>
                  )}
                </div>
                {lesson.is_preview && (
                  <div className="flex items-center text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    <Eye className="h-3 w-3 mr-1" /> Preview
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-1">
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectLesson(lesson.id);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8 text-destructive"
                  onClick={(e) => confirmDeleteLesson(lesson, e)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the lesson "{lessonToDelete?.title}" and all its content.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteLesson}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
